import { Alerta, Badge, Button, DataList, Dialog, Icono, MetricCard, Row, Select, SelectorArchivos, Spinner, Stack, TextField } from "@/components";
import { ACCEPT_ADJUNTO } from "@/lib/adjuntos";
import type { Gasto } from "@/lib/api";
import { formatFecha, formatMoneda } from "@/lib/format";
import { ETIQUETA_ESTADO, gastoCompleto, textoPagosACuenta, montosConFee, sinFee, TEXTO_SIN_FEE, OPCIONES_FILTRO_ESTADO, OPCIONES_FILTRO_TIPO, textoFormaPago, TONO_ESTADO, tarjetasTerrenoYObra } from "@/lib/gastos";
import { textoPresupuesto } from "@/lib/facturas";
import { FormularioGasto } from "./FormularioGasto";
import { useGastosTab } from "./useGastosTab";

/** Facturado, pagado y saldo en USD (la obra se piensa en esa moneda), con el detalle en ARS y USD. */
function totalUsd(monto: string, ars: string, usd: string) {
  return { valor: formatMoneda(monto, "USD"), detalle: `ARS ${formatMoneda(ars, "ARS")} · ${formatMoneda(usd, "USD")}` };
}

export function GastosTab() {
  const t = useGastosTab();

  // Lo importante primero: qué fue (la descripción) y cuánto (Mati, 2026-10-20). Rubro y proveedor sólo en la ficha; el
  // comprobante como ícono y el estado al final (Mati, 2026-10-29).
  // El fee va dentro de su gasto (Mati, 2026-10-28): el monto lo incluye. Lo normal es que lo lleve y no se marca; se
  // marca "Sin fee" lo que no lo lleva, como la compra del terreno (Mati, 2026-11-02). El desglose, en la ficha.
  const columnas = [
    { clave: "fecha", titulo: "Fecha", render: (g: Gasto) => formatFecha(g.fecha) },
    {
      clave: "descripcion",
      titulo: "Descripción",
      render: (g: Gasto) => (
        <Stack espacio={1}>
          <span className="font-medium">{g.descripcion}</span>
          {!t.esFiduciante && g.cotizacion && <span className="text-chico text-texto-suave">{textoPresupuesto(g)}</span>}
          {g.compra_terreno && <span className="text-chico text-texto-suave">{textoPagosACuenta(g.cantidad_pagos)}</span>}
        </Stack>
      ),
    },
    {
      clave: "monto",
      titulo: "Monto",
      render: (g: Gasto) => (
        <Row espacio={2}>
          <span>{formatMoneda(montosConFee(g).monto, g.moneda)}</span>
          {sinFee(g) && <Badge>{TEXTO_SIN_FEE}</Badge>}
        </Row>
      ),
    },
    {
      clave: "saldo",
      titulo: "Saldo",
      render: (g: Gasto) => formatMoneda(montosConFee(g).saldo, g.moneda),
    },
    { clave: "forma", titulo: "Forma de pago", render: (g: Gasto) => textoFormaPago(g.forma_pago) },
    { clave: "tipo", titulo: "Tipo", render: (g: Gasto) => <Badge>{g.tipo}</Badge> },
    {
      clave: "comprobante",
      titulo: "Comprobante",
      // la compra del terreno no tiene uno propio (los recibos están en cada pago); el fiduciante no ve si falta
      render: (g: Gasto) => {
        if (g.comprobante_id)
          return (
            <a href={t.rutaComprobante(g.comprobante_id)} download aria-label={`Descargar el comprobante de ${g.descripcion}`} title="Descargar el comprobante" className="inline-flex rounded-control p-1 text-primario hover:bg-primario-suave">
              <Icono nombre="comprobante" />
            </a>
          );
        if (g.compra_terreno || t.esFiduciante) return "—";
        return (
          <span role="img" aria-label="Falta el comprobante" title="Falta el comprobante" className="inline-flex p-1 text-alerta">
            <Icono nombre="falta-comprobante" />
          </span>
        );
      },
    },
    {
      clave: "estado",
      titulo: "Estado",
      render: (g: Gasto) => {
        const estado = montosConFee(g).estado;
        return <Badge tono={TONO_ESTADO[estado]}>{ETIQUETA_ESTADO[estado]}</Badge>;
      },
    },
  ];

  const facturado = t.totales && totalUsd(t.totales.monto_usd, t.totales.ARS.monto, t.totales.USD.monto);
  const pagado = t.totales && totalUsd(t.totales.pagado_usd, t.totales.ARS.pagado, t.totales.USD.pagado);
  const terrenoYObra = t.totales && tarjetasTerrenoYObra(t.totales);
  const saldo = t.totales && totalUsd(t.totales.saldo_usd, t.totales.ARS.saldo, t.totales.USD.saldo);

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Gastos</h1>
        {t.esAdmin && (
          <Row envolver espacio={2}>
            {t.puedeEscanear && (
              <SelectorArchivos etiqueta="Factura a escanear (PDF o foto)" textoBoton={t.escaneando ? "Leyendo…" : "Escanear factura"} accept={ACCEPT_ADJUNTO} deshabilitado={t.escaneando} onElegir={t.escanear} />
            )}
            <Button onClick={t.abrirNuevo}>Nuevo gasto</Button>
          </Row>
        )}
      </Row>
      {t.escaneando && (
        <Row espacio={2}>
          <span className="text-primario">
            <Spinner />
          </span>
          <span className="text-chico text-texto-suave">Leyendo la factura, tarda unos segundos…</span>
        </Row>
      )}
      <Alerta texto={t.errorEscanear} />

      {t.nombrePresupuestoFiltro && (
        <Row espacio={2}>
          <Badge tono="primario" onQuitar={t.quitarPresupuesto} etiquetaQuitar="Quitar filtro de presupuesto">{`Presupuesto: ${t.nombrePresupuestoFiltro}`}</Badge>
        </Row>
      )}

      {terrenoYObra && (
        <div className="grid grid-cols-2 gap-3">
          <MetricCard titulo="Terreno" valor={terrenoYObra.terreno.valor} detalle={terrenoYObra.terreno.detalle} />
          <MetricCard titulo="Obra" valor={terrenoYObra.obra.valor} detalle={terrenoYObra.obra.detalle} />
        </div>
      )}
      {facturado && pagado && saldo && (
        <div className="grid gap-3 sm:grid-cols-3">
          <MetricCard titulo="Total" valor={facturado.valor} detalle={facturado.detalle} />
          <MetricCard titulo="Pagado" valor={pagado.valor} detalle={pagado.detalle} />
          <MetricCard titulo="Saldo pendiente" valor={saldo.valor} detalle={saldo.detalle} />
        </div>
      )}

      <Row envolver espacio={3} alinear="fin">
        <TextField etiqueta="Buscar" placeholder="Descripción o nro de comprobante" value={t.filtros.q} onChange={(e) => t.cambiarFiltro("q", e.target.value)} contenedorClassName="min-w-56 flex-1" />
        <Select etiqueta="Proveedor" opciones={t.opcionesProveedorFiltro} valor={t.filtros.proveedorId} onCambio={(v) => t.cambiarFiltro("proveedorId", v)} className="min-w-48" />
        <Select etiqueta="Rubro" opciones={t.opcionesRubroFiltro} valor={t.filtros.rubroId} onCambio={(v) => t.cambiarFiltro("rubroId", v)} className="min-w-48" />
        <Select etiqueta="Tipo" opciones={OPCIONES_FILTRO_TIPO} valor={t.filtros.tipo} onCambio={(v) => t.cambiarFiltro("tipo", v)} className="w-28" />
        <Select etiqueta="Estado" opciones={OPCIONES_FILTRO_ESTADO} valor={t.filtros.estado} onCambio={(v) => t.cambiarFiltro("estado", v)} className="min-w-40" />
        <TextField etiqueta="Desde" type="date" value={t.filtros.desde} onChange={(e) => t.cambiarFiltro("desde", e.target.value)} />
        <TextField etiqueta="Hasta" type="date" value={t.filtros.hasta} onChange={(e) => t.cambiarFiltro("hasta", e.target.value)} />
        {t.hayFiltros && (
          <Button variante="fantasma" onClick={t.limpiarFiltros}>
            Limpiar filtros
          </Button>
        )}
      </Row>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <DataList etiqueta="Gastos" columnas={columnas} filas={t.items} claveFila={(g) => g.id} alElegirFila={t.verFicha} claseFila={(g) => (gastoCompleto(g, t.esFiduciante) ? "bg-exito-suave" : undefined)} vacio="No hay gastos para mostrar." />
      )}

      <Dialog abierto={t.abierto} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Nuevo gasto">
        <Alerta tono="alerta" texto={t.avisoEscaneo} />
        {t.proveedorLeidoNuevo && (
          <Row espacio={2} envolver className="mb-4">
            <span className="text-chico">{`En la factura figura «${t.proveedorLeidoNuevo}», que no está en la agenda.`}</span>
            <Button variante="secundario" tamanio="sm" onClick={t.agregarProveedorLeido}>
              Agregarlo a la agenda
            </Button>
          </Row>
        )}
        <FormularioGasto
          form={t.form}
          cambiar={t.cambiar}
          errores={t.errores}
          opcionesProveedor={t.opcionesProveedorForm}
          onCrearProveedor={t.crearProveedor}
          opcionesRubro={t.opcionesRubroForm}
          opcionesPresupuesto={t.opcionesPresupuesto}
          opcionesCuota={t.opcionesCuota}
          opcionesItem={t.opcionesItem}
          feePrevisto={t.feePrevisto}
          feePorcentaje={t.feePorcentaje}
          comprobante={
            <Stack espacio={1}>
              <SelectorArchivos etiqueta="Comprobante (PDF o foto)" textoBoton={t.comprobante ? "Cambiar el comprobante" : "Adjuntar comprobante"} accept={ACCEPT_ADJUNTO} onElegir={t.elegirComprobante} />
              {t.comprobante && <p className="text-chico text-texto-suave">{t.comprobante.name}</p>}
              <Alerta texto={t.errorComprobante} />
            </Stack>
          }
          onSubmit={(e) => void t.guardar(e)}
          onCancelar={t.cerrar}
          guardando={t.guardando}
          error={t.errorGuardar}
        />
      </Dialog>
    </Stack>
  );
}
